import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Clinic } from '../../models/clinic.model';

@Injectable({
  providedIn: 'root',
})
export class ClinicService {
  private apiUrl: string;
  private clinicsSubject = new BehaviorSubject<Clinic[]>([]);
  readonly clinics$ = this.clinicsSubject.asObservable();

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    };
  }

  getClinic(): Observable<any> {
    return this.http.get(this.apiUrl + '/clinic', this.getAuthHeaders()).pipe(
      tap((res: any) => {
        if (Array.isArray(res?.data)) {
          this.clinicsSubject.next(res.data);
        }
      }),
    );
  }

  getClinicById(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/clinic/${id}`, this.getAuthHeaders());
  }

  createClinic(payload: Partial<Clinic>): Observable<any> {
    return this.http.post(`${this.apiUrl}/clinic`, payload, this.getAuthHeaders()).pipe(
      tap(() => this.refreshClinics()),
    );
  }

  updateClinic(id: number, payload: Partial<Clinic>): Observable<any> {
    return this.http.put(`${this.apiUrl}/clinic/${id}`, payload, this.getAuthHeaders()).pipe(
      tap(() => this.refreshClinics()),
    );
  }

  deleteClinic(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/clinic/${id}`, this.getAuthHeaders()).pipe(
      tap(() => this.refreshClinics()),
    );
  }

  getTimeSlots(clinicId: number, date: string, slot?: number): Observable<any> {
    const params: Record<string, string> = { date };
    if (slot != null) {
      params['slot'] = String(slot);
    }
    return this.http.get(`${this.apiUrl}/clinic/${clinicId}/time-slots`, {
      ...this.getAuthHeaders(),
      params,
    });
  }

  refreshClinics(): void {
    this.getClinic().subscribe({ error: () => of(null) });
  }

  getCachedClinics(): Clinic[] {
    return this.clinicsSubject.value;
  }

  getCachedClinicById(id: number | null | undefined): Clinic | undefined {
    if (id == null) return undefined;
    return this.clinicsSubject.value.find((clinic) => clinic.id === Number(id));
  }

  getSlotDurationMinutes(clinicId: number | null | undefined): number {
    const clinic = this.getCachedClinicById(clinicId);
    const duration = Number(clinic?.slot_duration_minutes ?? 60);
    return Number.isFinite(duration) && duration >= 5 ? duration : 60;
  }
}
